'use client'

import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { getBaseImageUrl, getMerchantHref, splitHeading } from '@/constants/hooks'

export interface DiscountedStoreSliderProps {
  merchants: any[]
  widgetHeading: string
  widgetText: string
  companyDomain: string
  mer_slug: string
  mer_slug_type: string
}

const DiscountedStoreSlider = ({
  merchants,
  widgetHeading,
  widgetText,
  companyDomain,
  mer_slug,
  mer_slug_type,
}: DiscountedStoreSliderProps) => {
  const [first, second] = splitHeading(widgetHeading)

  if (!merchants?.length) return null

  // Duplicate enough times to fill wide screens seamlessly
  const track = [...merchants, ...merchants, ...merchants]

  return (
    <section className="py-8 md:py-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">

        {/* Header */}
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 mb-3"><span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" /><span className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em]">Discounted Stores</span></span>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 border-l-4 border-indigo-600 pl-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
              {first} <span className="text-indigo-600">{second}</span>
            </h2>
            {widgetText && (
              <p className="text-sm text-slate-500 font-medium mt-1 max-w-sm sm:max-w-none text-balance">{widgetText}</p>
            )}
          </div>
          <Link
            href="/all-stores/A"
            className="hidden md:flex ml-auto w-max group items-center gap-2 text-indigo-600 hover:text-indigo-700 font-bold transition-all whitespace-nowrap animate-fade-in-up animation-delay-300"
          >
            View all Stores
            <span className="p-1.5 bg-indigo-50 rounded-full group-hover:bg-indigo-100 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
          </Link>
        </div>

        {/* Marquee track — contained strictly aligned with heading */}
        <div className="relative">
          <div className="overflow-x-hidden overflow-y-visible py-2">
            <div className="flex gap-4 w-max ds-marquee hover:[animation-play-state:paused]">
              {track.map((merchant: any, i: number) => (
                <Link
                  key={i}
                  href={getMerchantHref(merchant, mer_slug, mer_slug_type)}
                  className="group flex flex-col items-center gap-2 w-24 shrink-0 relative z-0 hover:z-20"
                >
                  {/* Logo card */}
                  <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center p-3 shadow-sm border border-slate-100 group-hover:shadow-md group-hover:shadow-indigo-100/50 group-hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
                    <div className="absolute inset-0 group-hover:to-transparent transition-all duration-300 rounded-full pointer-events-none" />
                    <Image
                      src={getBaseImageUrl(companyDomain, merchant?.merchant_logo, '')}
                      alt={merchant?.merchant_name || 'Store Logo'}
                      width={80}
                      height={80}
                      className="relative z-10 object-contain w-full h-full group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  {/* Name */}
                  <p className="text-xs font-medium text-slate-600 group-hover:text-indigo-600 transition-colors text-center w-full truncate">
                    {merchant.merchant_name}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes ds-scroll {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-33.333%); }
          }
          .ds-marquee {
            animation: ds-scroll 60s linear infinite;
          }
        `
      }} />
    </section>
  )
}

export default DiscountedStoreSlider
